import { useCallback, useState } from 'react';
import { parseStrictAppDate } from '@/utils/date';
import { safeGetSessionStorage } from '@/utils/safeStorage';
import { useUserScopedSessionKey } from './useUserScopedSessionKey';

/**
 * The picker's Clear action reports an empty string, and storage is an
 * untrusted boundary, so neither is allowed to become the widget's date.
 */
const isAppDate = (value: string | null): value is string =>
  value !== null && parseStrictAppDate(value) !== null;

const readStoredDate = (storageKey: string): string | null => {
  const storage = safeGetSessionStorage();
  if (!storage) return null;
  try {
    const stored = storage.getItem(storageKey);
    return isAppDate(stored) ? stored : null;
  } catch {
    return null;
  }
};

const storeDate = (storageKey: string, date: string) => {
  const storage = safeGetSessionStorage();
  if (!storage) return;
  try {
    storage.setItem(storageKey, date);
  } catch {
    // Storage full or disabled — losing the date beats breaking the control.
  }
};

/**
 * A date that survives navigating away and back within the same session, which
 * is what widget date selections are expected to do. Namespaced to the
 * signed-in identity, so one advisor's date is never restored for the next user
 * in the same tab.
 */
export const useSessionDate = (
  baseKey: string,
  getInitialDate: () => string,
): [string, (date: string) => void] => {
  const storageKey = useUserScopedSessionKey(baseKey);
  const [date, setDate] = useState(
    () => readStoredDate(storageKey) ?? getInitialDate(),
  );

  const setPersistedDate = useCallback(
    (next: string) => {
      if (!isAppDate(next)) return;
      setDate(next);
      storeDate(storageKey, next);
    },
    [storageKey],
  );

  return [date, setPersistedDate];
};
